import { TOKEN_REFRESH_THRESHOLD, TOKEN_STATUS_RETRY_INTERVAL } from '../constants'
import { fetchWorkosTokenStatus } from '../lib/workos'
import useAuth from '../models/useAuth'
import networkMonitor from './networkMonitor'

class WorkOSTokenMonitor {
  private timeout?: NodeJS.Timeout | null = null
  private isRefreshing = false
  private connectHandler?: () => void
  private hadRefreshFailure = false
  private onRecovered?: (() => Promise<void> | void) | null
  private checking?: Promise<boolean>
  // The in-flight `refresh()`, so `check()` can JOIN it instead of returning while it is out.
  private refreshing?: Promise<void>

  async start(onRecovered?: () => Promise<void> | void) {
    if (this.timeout) {
      return
    }

    this.onRecovered = onRecovered ?? null

    // Check token and schedule refresh
    await this.scheduleNextRefresh()

    // Re-check when network reconnects (handles focus, wake, online events)
    this.connectHandler = () => {
      void this.check()
    }
    networkMonitor.on('connect', this.connectHandler)
  }

  stop() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }

    if (this.connectHandler) {
      networkMonitor.off('connect', this.connectHandler)
      this.connectHandler = undefined
    }

    this.isRefreshing = false
    this.refreshing = undefined
    this.hadRefreshFailure = false
    this.onRecovered = null
  }

  /**
   * Make sure the access token is usable: refresh it now when it has expired or is about to, and
   * schedule the next refresh. Resolves when that is done.
   *
   * ⚠⚠ ONE CHECK AT A TIME, SHARED BY EVERY CALLER (app#349). A wake emits `connect` once, and two
   * handlers answer it: this monitor's, and `Controller`'s data refresh. The controller's used to
   * run at the same moment as this one, so after a long sleep it read every store and opened every
   * realtime channel with the EXPIRED token. When the refresh then succeeded at the first try,
   * `onRecovered` did not run (nothing had failed), so nothing read the stores again. The
   * controller now awaits this, and a second `check()` joins the one in progress rather than
   * returning before the token is ready.
   *
   * ⚠⚠ RESOLVES TO WHETHER THE TOKEN IS CONFIRMED USABLE, and the caller must read it. Resolving
   * at all is NOT the signal: `scheduleNextRefresh` has two arms that mean "I found nothing out"
   * — a refresh already in flight, and a `fetchWorkosTokenStatus()` that returned null because the
   * helper swallows its own error. On a wake `navigator.onLine` is true while DNS is still cold,
   * so that fetch failing is the COMMON case, not the rare one; a caller that treats resolution as
   * "the token is ready" then does exactly what app#349 is about — reads every store and opens
   * every realtime channel on an expired token.
   *
   * ⚠ False does not mean the data never reloads. A check that could not confirm sets
   * `hadRefreshFailure`, so the retry timer's next positive determination runs `onRecovered`.
   */
  check(): Promise<boolean> {
    if (!this.checking) {
      this.checking = this.scheduleNextRefresh().finally(() => {
        this.checking = undefined
      })
    }
    return this.checking
  }

  /**
   * Resolves true only when the token state was positively established: it was fetched, and it is
   * either valid for a while yet or was refreshed here.
   */
  private async scheduleNextRefresh(): Promise<boolean> {
    // A refresh is already out: join it rather than reporting a state nobody established.
    // ⚠ `refresh()`'s OWN tail calls `determine()` and not this, or it would await itself.
    if (this.refreshing) {
      await this.refreshing
      return true
    }
    return this.determine()
  }

  /** The body of a check: fetch the token state, act on it, and say whether it was established. */
  private async determine(): Promise<boolean> {
    if (this.isRefreshing) {
      return false
    }

    // Clear any existing timeout to prevent multiple timers
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }

    try {
      // Fetch token status from router (reads HttpOnly cookies)
      const tokenStatus = await fetchWorkosTokenStatus()

      if (!tokenStatus) {
        console.warn('[TokenMonitor] Failed to fetch token status, scheduling retry')
        // ⚠ The data reload is OWED from here. Nothing established the token, so the caller is
        // told so and does not read the stores; this flag is what makes the retry below run
        // `onRecovered` once the token IS established, so the wake is not simply dropped.
        this.hadRefreshFailure = true
        // Schedule retry for transient failures to avoid monitor stopping
        this.timeout = setTimeout(() => {
          this.scheduleNextRefresh()
        }, TOKEN_STATUS_RETRY_INTERVAL)
        return false
      }

      const { expiresAt, hasAccessToken, hasRefreshToken } = tokenStatus

      // No tokens at all - user not signed in
      if (!hasAccessToken && !hasRefreshToken) {
        return false
      }

      // No access token but have refresh token - refresh immediately
      if (!hasAccessToken && hasRefreshToken) {
        await this.refresh()
        return true
      }

      // Calculate when to refresh based on token expiry
      if (expiresAt) {
        const now = Date.now()
        const timeUntilExpiry = expiresAt - now
        const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60)

        console.log(`[TokenMonitor] Access token expires in ${minutesUntilExpiry} minutes`)

        // If already expired or expiring soon, refresh immediately
        if (timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD) {
          await this.refresh()
          return true
        }

        // Schedule refresh for when token enters refresh window
        const msUntilRefresh = timeUntilExpiry - TOKEN_REFRESH_THRESHOLD

        console.log(`[TokenMonitor] Scheduling refresh in ${Math.round(msUntilRefresh / 1000 / 60)} minutes`)

        this.timeout = setTimeout(() => {
          this.refresh()
        }, msUntilRefresh)
      }
      // The token is established and needs no refresh yet. If an earlier check or refresh could
      // not say that, the stores were never re-read for it: that is what is owed here.
      await this.recover()
      return true
    } catch (error) {
      console.error('[TokenMonitor] Error scheduling refresh:', error)
      this.hadRefreshFailure = true
      return false
    }
  }

  /** Reload the app's data once, if a check or a refresh left it owed. */
  private async recover() {
    if (!this.hadRefreshFailure || !this.onRecovered) return
    try {
      await this.onRecovered()
    } catch (error) {
      console.error('[TokenMonitor] Error while reloading app state after token recovery:', error)
    } finally {
      this.hadRefreshFailure = false
    }
  }

  private refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing
    const p: Promise<void> = this.refreshBody().finally(() => {
      // ⚠ Only if it is still MINE. A successor started during the tail must not be dropped.
      if (this.refreshing === p) this.refreshing = undefined
    })
    this.refreshing = p
    return p
  }

  private async refreshBody() {
    if (this.isRefreshing) {
      return
    }

    this.isRefreshing = true

    let success = false
    try {
      console.log('[TokenMonitor] Refreshing access token...')
      success = await useAuth.getState().refreshWorkosToken()
      if (success) {
        console.log('[TokenMonitor] Token refresh successful')
      }
    } catch (error) {
      console.error('[TokenMonitor] Error refreshing token:', error)
    } finally {
      this.isRefreshing = false
    }

    if (!success) {
      this.hadRefreshFailure = true
      // Keep monitoring after transient failures.
      this.timeout = setTimeout(() => {
        this.scheduleNextRefresh()
      }, TOKEN_STATUS_RETRY_INTERVAL)
      return
    }

    await this.recover()

    // ⚠ `determine()` and not `scheduleNextRefresh()`: this runs INSIDE `this.refreshing`, and the
    // join guard there would make it await the promise it is the body of.
    await this.determine()
  }
}

export default new WorkOSTokenMonitor()
